import React from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import type { Store as StoreType } from '../../../../services/authService';
import storePosMappingOptionsService, {
    type StorePosMappingOptionsResponse,
} from '../../../../services/storePosMappingOptionsService';
import { useStoreDetailsPosTypes } from '../../hooks/useStoreDetailsPosTypes';
import { useStoreDetailsLocationMetadata } from './useStoreDetailsLocationMetadata';
import { storeDetailsStoreSchema } from '../validators/storeDetailsStoreSchema';
import type { StoreFormData } from '../../../../types/features/storeDetails/storeForm';
import {
    getDefaultStoreFormValues,
    LOTTORECO_POS_TYPE,
    mapStoreToFormValues,
} from '../utils/storeDetailsStoreFormUtils';

type UseEditStoreDialogParams = {
    countries?: string[];
    onClose: () => void;
    onSave: (storeId: string, data: StoreFormData) => Promise<void>;
    open: boolean;
    posTypes?: string[];
    statesByCountry?: Record<string, string[]>;
    store: StoreType | null;
};

const EMPTY_MAPPING_OPTIONS: StorePosMappingOptionsResponse = {
    financial_event: [],
    transaction_line: [],
    lotto_merch_description: [],
};

export const useEditStoreDialog = ({
    countries: prefetchedCountries = [],
    onClose,
    onSave,
    open,
    posTypes: prefetchedPosTypes = [],
    statesByCountry: prefetchedStatesByCountry = {},
    store,
}: UseEditStoreDialogParams) => {
    const { posTypes: loadedPosTypes } = useStoreDetailsPosTypes(open && prefetchedPosTypes.length === 0);
    const { countries: loadedCountries, statesByCountry: loadedStatesByCountry } = useStoreDetailsLocationMetadata(
        open && prefetchedCountries.length === 0 && Object.keys(prefetchedStatesByCountry).length === 0,
    );
    const loadedOrPrefetchedPosTypes = prefetchedPosTypes.length > 0 ? prefetchedPosTypes : loadedPosTypes;
    const posTypes = React.useMemo(
        () => Array.from(new Set([...loadedOrPrefetchedPosTypes, LOTTORECO_POS_TYPE])),
        [loadedOrPrefetchedPosTypes],
    );
    const countries = prefetchedCountries.length > 0 ? prefetchedCountries : loadedCountries;
    const statesByCountry =
        Object.keys(prefetchedStatesByCountry).length > 0 ? prefetchedStatesByCountry : loadedStatesByCountry;
    const [mappingOptions, setMappingOptions] = React.useState<StorePosMappingOptionsResponse>(EMPTY_MAPPING_OPTIONS);

    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors, isValid },
    } = useForm<StoreFormData>({
        resolver: yupResolver(storeDetailsStoreSchema),
        mode: 'onChange',
        defaultValues: getDefaultStoreFormValues(),
    });

    const selectedCountry = watch('country');

    React.useEffect(() => {
        const states = statesByCountry[selectedCountry] || [];
        const currentState = (watch('state') as string) || '';
        if (currentState && states.length > 0 && !states.includes(currentState)) {
            setValue('state', '');
        }
        if (!selectedCountry) {
            setValue('state', '');
        }
    }, [selectedCountry, setValue, statesByCountry, watch]);

    React.useEffect(() => {
        if (open) {
            reset(mapStoreToFormValues(store));
        }
    }, [open, reset, store]);

    React.useEffect(() => {
        if (!open || !store?.id) {
            setMappingOptions(EMPTY_MAPPING_OPTIONS);
            return undefined;
        }

        let isActive = true;

        storePosMappingOptionsService
            .getOptions(store.id)
            .then((options) => {
                if (isActive) {
                    setMappingOptions(options);
                }
            })
            .catch((error) => {
                console.error('Error fetching POS mapping options:', error);
                if (isActive) {
                    setMappingOptions(EMPTY_MAPPING_OPTIONS);
                }
            });

        return () => {
            isActive = false;
        };
    }, [open, store?.id]);

    const onSubmit = handleSubmit(async (data) => {
        if (!store) return;
        try {
            await onSave(store.id, data);
        } catch {}
    });

    const handleClose = React.useCallback(() => {
        reset(mapStoreToFormValues(store));
        onClose();
    }, [onClose, reset, store]);

    return {
        control,
        countries,
        errors,
        handleClose,
        isValid,
        mappingOptions,
        onSubmit,
        posTypes,
        selectedCountry,
        statesByCountry,
    };
};
